import React, { ReactNode, useEffect } from 'react';
import { TfiClose } from 'react-icons/tfi';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: number;
  contentPadding?: number;
  topOffset?: number; // distance from top (e.g., fixed header height)
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth = 960,
  contentPadding = 24,
  topOffset = 0,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        top: 0, // Always start from top to cover header
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        background: 'rgba(0,0,0,0.75)', // match addresses overlay
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: `${Math.max(20, topOffset + 20)}px 20px 20px 20px`, // Account for header height in top padding
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: `min(${maxWidth}px, calc(100vw - 40px))`,
          background: 'var(--color-background)',
          borderRadius: 16,
          border: '1px solid var(--color-gray-dark-800)',
          overflow: 'hidden',
          boxShadow:
            '0px 3px 3px -1.5px var(--ColorsEffectsShadowsshadow-xl03), 0px 8px 8px -4px var(--ColorsEffectsShadowsshadow-xl02), 0px 20px 24px -4px var(--ColorsEffectsShadowsshadow-xl01)',
          maxHeight: `calc(100vh - ${Math.max(20, topOffset + 20)}px - 20px)`,
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {title ? (
          <div
            style={{
              padding: '24px',
              borderBottom: '1px solid var(--color-gray-dark-800)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <div>{title}</div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 'none',
                color: '#61656C',
                cursor: 'pointer',
                fontSize: 14,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <TfiClose />
            </button>
          </div>
        ) : null}
        <div style={{ padding: contentPadding, overflowY: 'auto', overflowX: 'hidden' }}>
          {children}
        </div>
        {footer ? (
          <div
            style={{
              padding: 16,
              borderTop: '1px solid var(--color-gray-dark-800)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
            }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Modal;
